import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const CLIENT_ID_STORAGE_KEY = "waitlist_client_id";

export type WaitlistSubmitInput = {
  email: string;
  name?: string;
  comments?: string | null;
  howDidYouHear?: string | null;
  /**
   * Which landing page / flow this signup came from (e.g. "wharton"). Drives
   * which welcome email notify-waitlist-join sends. Omit for the main site.
   */
  source?: string | null;
  /**
   * Which pricing "edit" the signup came from (e.g. "Capsule Wardrobe Build"),
   * when they joined by booking an edit in <PricingEdits>. Stored in edit_source.
   */
  editSource?: string | null;
};

export type WaitlistSubmitResult = {
  alreadyOnList: boolean;
};

export function useWaitlistSubmit() {
  const submit = useCallback(
    async (input: WaitlistSubmitInput): Promise<WaitlistSubmitResult> => {
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = crypto.randomUUID();
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }

      const email = input.email.trim();
      const fallbackName = email.split("@")[0] ?? "";

      const baseRow = {
        email,
        name: input.name?.trim() || fallbackName,
        comments: input.comments ?? null,
        how_did_you_hear: input.howDidYouHear ?? null,
        signup_source: input.source ?? null,
        client_id: clientId,
      };

      let { error } = await supabase
        .from("waitlist_submissions")
        .insert({ ...baseRow, edit_source: input.editSource ?? null });

      // If the edit_source column isn't there yet (migration not applied), don't
      // break the signup — retry without it. 42703 = Postgres undefined_column;
      // PGRST204 = column missing from PostgREST's schema cache.
      if (error && (error.code === "42703" || error.code === "PGRST204")) {
        ({ error } = await supabase.from("waitlist_submissions").insert(baseRow));
      }

      if (error) {
        if (error.code === "23505") {
          return { alreadyOnList: true };
        }
        throw error;
      }

      return { alreadyOnList: false };
    },
    []
  );

  return { submit };
}
